import React, { useEffect, useRef } from 'react';
import {
  AvailableBadge,
  DealerInfoRethink,
  DynamicComponent,
  dynamicStylingHelper,
  DynamicWrapper,
  FinanceRethink,
  KeyFeaturesWrapper,
  setSelectedVehicle,
  useServicesContext,
  VehicleBasic,
} from '@oneaudi/vtp-shared';

import {
  AlphaWrapper,
  GreyText,
  StyledLink,
  StyledTileHeadlineArea,
  TileContainer,
  TileContent,
} from './Tile.styles';
import Gallery from './Gallery/Gallery';
import { trackDetailsPageNav } from '../../tracking/tilesTracking';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { useVehicleDetailsPageUrl } from '../../hooks/useVehicleDetailsPageUrl';
import ActionButtons from './ActionButtons';
import { Headline } from '@oneaudi/unified-web-components';
import ConsumptionAndEmission from './ConsumptionAndEmission';

interface TileProps {
  vehicle: VehicleBasic;
  focusOnMount?: boolean;
}

const Tile: React.FC<TileProps> = ({ vehicle, focusOnMount = false }: TileProps) => {
  // this needs to be resolved before potentially returning null in the case that vehicleId is not in vehicles.
  const detailsPageLink = useVehicleDetailsPageUrl(vehicle) || '';
  const imageAnchorRef = useRef<HTMLAnchorElement>(null); // ref to the image link

  const servicesContext = useServicesContext();
  const trackingService = servicesContext.getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;

  useEffect(() => {
    if (focusOnMount && imageAnchorRef.current) {
      imageAnchorRef.current.focus();
    }
  }, [focusOnMount]);

  if (!vehicle) {
    return null;
  }

  return (
    <TileContainer data-testid="Tile" id={vehicle.id}>
      <Gallery
        vehicle={vehicle}
        detailsPageLink={detailsPageLink}
        isListView={false}
        focusRef={imageAnchorRef}
      />
      <TileContent>
        <DynamicWrapper AlphaWrapperFc={AlphaWrapper}>
          <DynamicWrapper AlphaWrapperFc={StyledTileHeadlineArea}>
            <StyledLink
              href={detailsPageLink}
              variant="standalone"
              size="md"
              data-testid="carline"
              onClick={() => {
                setSelectedVehicle(vehicle.id);
                trackDetailsPageNav(
                  trackingService,
                  `${vehicle.symbolicCarline?.description}`,
                  detailsPageLink,
                  'text link',
                  'primary button'
                );
              }}
            >
              <Headline
                tag="h3"
                variant={{
                  order: '3',
                  type: 'headline',
                  weight: dynamicStylingHelper('normal', 'bold'),
                }}
                data-testid="allVehiclesHeadline"
              >
                {vehicle.symbolicCarline?.description}
              </Headline>
            </StyledLink>
            {vehicle.modelCode?.description && (
              <GreyText variant="copy2" spaceStackEnd="l" data-testid="modelCode">
                {vehicle.modelCode?.description}
              </GreyText>
            )}
          </DynamicWrapper>
          <FinanceRethink vehicleBasic={vehicle} isPLP />
        </DynamicWrapper>
        <DynamicWrapper AlphaWrapperFc={AlphaWrapper}>
          <DynamicComponent legacy={<KeyFeaturesWrapper isPlp vehicle={vehicle} />} />
          <AvailableBadge vehicleBasic={vehicle} isPLP />
          <DealerInfoRethink vehicleBasic={vehicle} isPLP />
          <DynamicComponent alpha={<KeyFeaturesWrapper isPlp vehicle={vehicle} />} />
          <ActionButtons vehicle={vehicle} />
          <ConsumptionAndEmission vehicle={vehicle} />
        </DynamicWrapper>
      </TileContent>
    </TileContainer>
  );
};

export default Tile;
